
import { useState, useMemo, useEffect } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Customer, socialMediaSources, referralSources, advertisingSources } from "../types";
import { leadSourceOptions } from "@/components/customers/leadSourceOptions";
import { useAddressHierarchy } from "@/hooks/useAddressHierarchy";
import { 
  normalizeCity, 
  normalizeSuburb, 
  normalizeProvinceState, 
  normalizePostalCode,
  normalizeCustomerAddress 
} from "@/lib/addressUtils";

export function useCustomerData() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedLeadSources, setSelectedLeadSources] = useState<string[]>([]);
  const [filterDateAdded, setFilterDateAdded] = useState<[Date | undefined, Date | undefined]>([undefined, undefined]);
  const [filterDateModified, setFilterDateModified] = useState<[Date | undefined, Date | undefined]>([undefined, undefined]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedLeadOwners, setSelectedLeadOwners] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedSuburbs, setSelectedSuburbs] = useState<string[]>([]);
  const [selectedProvinceStates, setSelectedProvinceStates] = useState<string[]>([]);
  const [selectedPostalCodes, setSelectedPostalCodes] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [allCities, setAllCities] = useState<string[]>([]);
  const [allSuburbs, setAllSuburbs] = useState<string[]>([]);
  const [allProvinceStates, setAllProvinceStates] = useState<string[]>([]);
  const [allPostalCodes, setAllPostalCodes] = useState<string[]>([]);
  
  const { session, user } = useAuth();
  
  // Use address hierarchy for intelligent filtering
  const addressHierarchy = useAddressHierarchy(customers);

  const fetchCustomers = async () => {
    try {
      if (!session?.user?.id) return;
      
      console.log('Fetching customers with user context:', {
        userId: session.user.id,
        isImpersonating: user?.impersonated,
        userProfile: user
      });
      
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) {
        console.error("Error fetching customers:", error);
        return;
      }
      
      console.log(`Received ${data?.length || 0} customers from database`);
      // Cast customer_type to the correct union type
      const typedCustomers = (data || []).map(c => ({
        ...c,
        customer_type: (c.customer_type || 'residential') as 'residential' | 'commercial'
      }));
      setCustomers(typedCustomers);
      
      // Safely extract unique tags with comprehensive null checks
      const uniqueTags = Array.from(
        new Set(
          (data || [])
            .filter(customer => customer && customer.tags && Array.isArray(customer.tags) && customer.tags.length > 0)
            .flatMap(customer => customer.tags || [])
            .filter(tag => tag && typeof tag === 'string')
        )
      );
      setAllTags(uniqueTags || []);
      
      // Extract and normalize unique address fields to prevent duplicates
      const normalizedCustomers = (data || []).map(normalizeCustomerAddress);
      
      // Safely extract unique cities with normalization
      const uniqueCities = Array.from(
        new Set(
          normalizedCustomers
            .filter(customer => customer && customer.city && typeof customer.city === 'string')
            .map(customer => normalizeCity(customer.city))
            .filter(Boolean)
        )
      );
      setAllCities(uniqueCities.sort() || []);
      
      // Safely extract unique suburbs with normalization
      const uniqueSuburbs = Array.from(
        new Set(
          normalizedCustomers
            .filter(customer => customer && customer.suburb && typeof customer.suburb === 'string')
            .map(customer => normalizeSuburb(customer.suburb))
            .filter(Boolean)
        )
      );
      setAllSuburbs(uniqueSuburbs.sort() || []);
      
      // Safely extract unique province states with normalization
      const uniqueProvinceStates = Array.from(
        new Set(
          normalizedCustomers
            .filter(customer => customer && customer.province_state && typeof customer.province_state === 'string')
            .map(customer => normalizeProvinceState(customer.province_state))
            .filter(Boolean)
        )
      );
      setAllProvinceStates(uniqueProvinceStates.sort() || []);
      
      // Safely extract unique postal codes with normalization
      const uniquePostalCodes = Array.from(
        new Set(
          normalizedCustomers
            .filter(customer => customer && customer.postal_code && typeof customer.postal_code === 'string')
            .map(customer => normalizePostalCode(customer.postal_code))
            .filter(Boolean)
        )
      );
      setAllPostalCodes(uniquePostalCodes.sort() || []);
      
      return data;
    } catch (error) {
      console.error("Error in fetchCustomers:", error);
    }
  };

  const toggleLeadSourceFilter = (source: string) => {
    setSelectedLeadSources(prev => {
      if (prev.includes(source)) {
        return prev.filter(s => s !== source);
      } else {
        return [...prev, source];
      }
    });
  };

  const toggleTagFilter = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };
  
  const toggleLeadOwnerFilter = (ownerId: string) => {
    setSelectedLeadOwners(prev => 
      prev.includes(ownerId) ? prev.filter(id => id !== ownerId) : [...prev, ownerId]
    );
  };

  const toggleCityFilter = (city: string) => {
    setSelectedCities(prev => 
      prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]
    );
  };

  const toggleSuburbFilter = (suburb: string) => {
    setSelectedSuburbs(prev => 
      prev.includes(suburb) ? prev.filter(s => s !== suburb) : [...prev, suburb]
    );
  };

  const toggleProvinceStateFilter = (provinceState: string) => {
    setSelectedProvinceStates(prev => 
      prev.includes(provinceState) ? prev.filter(ps => ps !== provinceState) : [...prev, provinceState]
    );
  };

  const togglePostalCodeFilter = (postalCode: string) => {
    setSelectedPostalCodes(prev => 
      prev.includes(postalCode) ? prev.filter(pc => pc !== postalCode) : [...prev, postalCode]
    );
  };

  const resetFilters = () => {
    setSelectedLeadSources([]);
    setSelectedTags([]);
    setSelectedLeadOwners([]);
    setSelectedCities([]);
    setSelectedSuburbs([]);
    setSelectedProvinceStates([]);
    setSelectedPostalCodes([]);
    setFilterDateAdded([undefined, undefined]);
    setFilterDateModified([undefined, undefined]);
  };

  const hasActiveFilters = selectedLeadSources.length > 0 || 
    selectedTags.length > 0 ||
    selectedLeadOwners.length > 0 ||
    selectedCities.length > 0 ||
    selectedSuburbs.length > 0 ||
    selectedProvinceStates.length > 0 ||
    selectedPostalCodes.length > 0 ||
    filterDateAdded[0] !== undefined || 
    filterDateAdded[1] !== undefined || 
    filterDateModified[0] !== undefined || 
    filterDateModified[1] !== undefined;

  const renderDateLabel = (range: [Date | undefined, Date | undefined]) => {
    if (!range[0] && !range[1]) return "Any";
    if (range[0] && range[1])
      return `${format(range[0], "dd MMMM yyyy")} - ${format(range[1], "dd MMMM yyyy")}`;
    if (range[0]) return `After ${format(range[0], "dd MMMM yyyy")}`;
    if (range[1]) return `Before ${format(range[1], "dd MMMM yyyy")}`;
    return "Any";
  };

  const filteredSortedCustomers = useMemo(() => {
    // Ensure customers is always an array to prevent iteration errors
    const safeCustomers = Array.isArray(customers) ? customers : [];
    
    return safeCustomers
      .filter((customer) => {
        if (selectedLeadSources.length > 0) {
          const customerSource = customer.lead_source || "";
          
          let matchesSelectedSource = false;

          for (const selectedSource of selectedLeadSources) {
            if (
              selectedSource === "Word of Mouth" &&
              referralSources.includes(customerSource)
            ) {
              matchesSelectedSource = true;
              break;
            }

            if (
              selectedSource === "Social Media" &&
              socialMediaSources.includes(customerSource)
            ) {
              matchesSelectedSource = true;
              break;
            }

            if (selectedSource === "Car Branding" && customerSource === "Car Branding") {
              matchesSelectedSource = true;
              break;
            }

            if (selectedSource === "House Branding" && customerSource === "House Branding") {
              matchesSelectedSource = true;
              break;
            }

            if (
              selectedSource !== "Social Media" &&
              selectedSource !== "Word of Mouth" &&
              selectedSource !== "Car Branding" &&
              selectedSource !== "House Branding" &&
              selectedSource !== "Other" &&
              selectedSource === customerSource
            ) {
              matchesSelectedSource = true;
              break;
            }

            if (
              selectedSource === "Other" &&
              !leadSourceOptions
                .slice(0, -1)
                .map(o => o.value)
                .includes(customerSource)
            ) {
              matchesSelectedSource = true;
              break;
            }
          }

          if (!matchesSelectedSource) {
            return false;
          }
        }
        
        if (selectedTags.length > 0) {
          if (!customer.tags || customer.tags.length === 0) {
            return false;
          }
          
          let hasMatchingTag = false;
          for (const tag of selectedTags) {
            if (customer.tags.includes(tag)) {
              hasMatchingTag = true;
              break;
            }
          }
          
          if (!hasMatchingTag) {
            return false;
          }
        }
        
        if (selectedLeadOwners.length > 0) {
          if (!customer.lead_owner_id || !selectedLeadOwners.includes(customer.lead_owner_id)) {
            return false;
          }
        }

        // Normalize address fields before filtering to ensure consistency
        if (selectedCities.length > 0) {
          const normalizedCity = normalizeCity(customer.city);
          if (!normalizedCity || !selectedCities.includes(normalizedCity)) {
            return false;
          }
        }

        if (selectedSuburbs.length > 0) {
          const normalizedSuburb = normalizeSuburb(customer.suburb);
          if (!normalizedSuburb || !selectedSuburbs.includes(normalizedSuburb)) {
            return false;
          }
        }

        if (selectedProvinceStates.length > 0) {
          const normalizedProvince = normalizeProvinceState(customer.province_state);
          if (!normalizedProvince || !selectedProvinceStates.includes(normalizedProvince)) {
            return false;
          }
        }

        if (selectedPostalCodes.length > 0) {
          const normalizedPostalCode = normalizePostalCode(customer.postal_code);
          if (!normalizedPostalCode || !selectedPostalCodes.includes(normalizedPostalCode)) {
            return false;
          }
        }

        if (filterDateAdded[0] && new Date(customer.created_at).getTime() < new Date(filterDateAdded[0]).getTime()) {
          return false;
        }
        if (filterDateAdded[1] && new Date(customer.created_at).getTime() > new Date(filterDateAdded[1]).getTime() + 86400000) {
          return false;
        }
        
        if (filterDateModified[0] && new Date(customer.updated_at).getTime() < new Date(filterDateModified[0]).getTime()) {
          return false;
        }
        if (filterDateModified[1] && new Date(customer.updated_at).getTime() > new Date(filterDateModified[1]).getTime() + 86400000) {
          return false;
        }
        
        const searchLower = search.toLowerCase();
        return !search || [
          customer.name,
          customer.last_name,
          customer.business_name,
          customer.vat_number,
          customer.business_email,
          customer.business_phone,
          customer.street_address,
          customer.suburb,
          customer.city,
          customer.province_state,
          customer.postal_code,
          customer.lead_source,
          customer.lead_source_description,
          ...(customer.emails || []),
          ...(customer.cell_numbers || [])
        ].some(field => 
          field?.toLowerCase().includes(searchLower)
        );
      })
      .sort((a, b) => {
        let valA: any, valB: any;
        
        if (sortBy === "name") {
          valA = a.name?.toLowerCase() || "";
          valB = b.name?.toLowerCase() || "";
        } else if (sortBy === "last_name") {
          valA = a.last_name?.toLowerCase() || "";
          valB = b.last_name?.toLowerCase() || "";
        } else if (sortBy === "created_at") {
          valA = new Date(a.created_at || 0).getTime();
          valB = new Date(b.created_at || 0).getTime();
        } else if (sortBy === "updated_at") {
          valA = new Date(a.updated_at || 0).getTime();
          valB = new Date(b.updated_at || 0).getTime();
        } else {
          valA = "";
          valB = "";
        }
        
        if (valA === valB) return 0;
        if (sortDir === "asc") return valA > valB ? 1 : -1;
        else return valA < valB ? 1 : -1;
      });
  }, [
    customers,
    search,
    sortBy,
    sortDir,
    selectedLeadSources,
    selectedTags,
    selectedLeadOwners,
    selectedCities,
    selectedSuburbs,
    selectedProvinceStates,
    selectedPostalCodes,
    filterDateAdded,
    filterDateModified,
  ]);

  return {
    customers,
    filteredSortedCustomers,
    fetchCustomers,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
    selectedLeadSources,
    setSelectedLeadSources,
    selectedTags,
    setSelectedTags,
    selectedLeadOwners,
    setSelectedLeadOwners,
    selectedCities,
    setSelectedCities,
    selectedSuburbs,
    setSelectedSuburbs,
    selectedProvinceStates,
    setSelectedProvinceStates,
    selectedPostalCodes,
    setSelectedPostalCodes,
    filterDateAdded,
    setFilterDateAdded,
    filterDateModified,
    setFilterDateModified,
    toggleLeadSourceFilter,
    toggleTagFilter,
    toggleLeadOwnerFilter,
    toggleCityFilter,
    toggleSuburbFilter,
    toggleProvinceStateFilter,
    togglePostalCodeFilter,
    resetFilters,
    hasActiveFilters,
    allTags,
    allCities,
    allSuburbs,
    allProvinceStates,
    allPostalCodes,
    renderDateLabel,
    addressHierarchy
  };
}
